import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  Link,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useState } from "react";
import { useSession } from "@/contexts/SessionContext";
import { TenantSidebar, SuperSidebar } from "@/components/Sidebar";
import { Topbar } from "@/components/Topbar";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { CommandPalette, useCommandPaletteHotkey } from "@/components/CommandPalette";
import { NetworkBanner } from "@/components/NetworkBanner";
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { AcceptInvitationPage } from "@/pages/auth/AcceptInvitationPage";
import { OnboardingFirstTenantPage } from "@/pages/OnboardingFirstTenantPage";
import { DashboardPage } from "@/pages/tenant/DashboardPage";
import { MembersPage } from "@/pages/tenant/MembersPage";
import { ProfilePage } from "@/pages/tenant/ProfilePage";
import { AuditLogsPage } from "@/pages/tenant/AuditLogsPage";
import { SitesPage } from "@/pages/tenant/SitesPage";
import { ChatbotsListPage } from "@/pages/tenant/ChatbotsListPage";
import { ChatbotDetailPage } from "@/pages/tenant/ChatbotDetailPage";
import { IntegrationsLLMPage } from "@/pages/tenant/IntegrationsLLMPage";
import { IntegrationsTwilioPage } from "@/pages/tenant/IntegrationsTwilioPage";
import { PhoneNumbersPage } from "@/pages/tenant/PhoneNumbersPage";
import { ConversationsPage } from "@/pages/tenant/ConversationsPage";
import { NotificationsPage } from "@/pages/tenant/NotificationsPage";
import { UsagePage } from "@/pages/tenant/UsagePage";
import { SuperTenantsPage } from "@/pages/super/SuperTenantsPage";
import { SuperTenantDetailPage } from "@/pages/super/SuperTenantDetailPage";
import { SuperAuditLogsPage } from "@/pages/super/SuperAuditLogsPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

const ROUTE_LABELS: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/members": "Members",
  "/profile": "Profile",
  "/audit-logs": "Audit log",
  "/sites": "Sites",
  "/chatbots": "Chatbots",
  "/integrations/llm": "LLM integrations",
  "/integrations/twilio": "Twilio integration",
  "/phone-numbers": "Phone numbers",
  "/conversations": "Conversations",
  "/notifications": "Notifications",
  "/usage": "Usage",
  "/super/tenants": "Tenants",
  "/super/audit-logs": "Audit log",
};

function routeLabel(pathname: string): string {
  for (const [k, v] of Object.entries(ROUTE_LABELS)) {
    if (pathname === k || pathname.startsWith(k + "/")) return v;
  }
  return "—";
}

function TenantLayout(): JSX.Element {
  const { isLoading, isAuthenticated, me } = useSession();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteHotkey(() => setPaletteOpen(true));

  if (isLoading) return <div className="empty">Loading…</div>;
  if (!isAuthenticated) {
    // Redirect on next tick to let TanStack Router pick it up.
    setTimeout(() => {
      window.location.assign("/login");
    }, 0);
    return <div className="empty">Redirecting…</div>;
  }
  if (me && me.memberships.length === 0) {
    setTimeout(() => {
      window.location.assign("/onboarding/first-tenant");
    }, 0);
    return <div className="empty">Redirecting…</div>;
  }
  const currentMembership = me?.memberships.find((m) => m.tenant_id === me.current_tenant_id);
  return (
    <>
      <ImpersonationBanner />
      <div className="app-shell">
        <TenantSidebar
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />
        {mobileSidebarOpen ? (
          <div
            className="sidebar-mobile-backdrop"
            onClick={() => setMobileSidebarOpen(false)}
            aria-hidden="true"
          />
        ) : null}
        <main className="app-main">
          <Topbar
            here={routeLabel(window.location.pathname)}
            workspaceLabel={currentMembership?.name ?? "Workspace"}
            onToggleMobileSidebar={() => setMobileSidebarOpen((v) => !v)}
            onOpenSearch={() => setPaletteOpen(true)}
          />
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <NetworkBanner />
    </>
  );
}

function SuperLayout(): JSX.Element {
  const { isLoading, isAuthenticated, me } = useSession();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  if (isLoading) return <div className="empty">Loading…</div>;
  if (!isAuthenticated || !me) {
    setTimeout(() => {
      window.location.assign("/login");
    }, 0);
    return <div className="empty">Redirecting…</div>;
  }
  if (!me.super_admin) {
    return <NotFoundPage />;
  }
  return (
    <>
      <div className="app-shell">
        <SuperSidebar />
        {mobileSidebarOpen ? (
          <div
            className="sidebar-mobile-backdrop"
            onClick={() => setMobileSidebarOpen(false)}
            aria-hidden="true"
          />
        ) : null}
        <main className="app-main">
          <Topbar
            here={routeLabel(window.location.pathname)}
            workspaceLabel="BestAI · super admin"
            onToggleMobileSidebar={() => setMobileSidebarOpen((v) => !v)}
          />
          <Outlet />
        </main>
      </div>
      <NetworkBanner />
    </>
  );
}

function PublicLayout({ children }: { children?: ReactNode }): JSX.Element {
  return <>{children ?? <Outlet />}</>;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

// Public routes
const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_public",
  component: PublicLayout,
});
const loginRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => {
    const out: { redirect?: string } = {};
    if (typeof s.redirect === "string") out.redirect = s.redirect;
    return out;
  },
});
const registerRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/register",
  component: RegisterPage,
});
const verifyEmailRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/verify-email",
  component: VerifyEmailPage,
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
});
const acceptInvitationRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/invitations/$token/accept",
  component: AcceptInvitationPage,
});

// Onboarding (no sidebar, requires auth)
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding/first-tenant",
  component: function OnboardingGate() {
    const { isAuthenticated, isLoading } = useSession();
    if (isLoading) return <div className="empty">Loading…</div>;
    if (!isAuthenticated) {
      setTimeout(() => window.location.assign("/login"), 0);
      return <div className="empty">Redirecting…</div>;
    }
    return <OnboardingFirstTenantPage />;
  },
});

// Tenant routes
const tenantLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_tenant",
  component: TenantLayout,
});
const dashboardRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/dashboard",
  component: DashboardPage,
});
const membersRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/members",
  component: MembersPage,
});
const profileRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/profile",
  component: ProfilePage,
});
const tenantAuditRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/audit-logs",
  component: AuditLogsPage,
});

// WP-B: chatbots / sites / integrations. Support role is denied access to
// settings & integrations to match the RBAC matrix in docs/saas-plan.md §3.1.
function RoleGate({
  allow,
  children,
}: {
  allow: (role: string) => boolean;
  children: ReactNode;
}): JSX.Element {
  const { me } = useSession();
  const m = me?.memberships.find((mm) => mm.tenant_id === me.current_tenant_id);
  const role = m?.role ?? "viewer";
  if (!allow(role)) return <NotFoundPage />;
  return <>{children}</>;
}

const sitesRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/sites",
  component: function SitesGate() {
    return (
      <RoleGate allow={(r) => r !== "support"}>
        <SitesPage />
      </RoleGate>
    );
  },
});

const chatbotsListRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/chatbots",
  component: ChatbotsListPage,
});

const chatbotDetailRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/chatbots/$id",
  component: ChatbotDetailPage,
});

const integrationsLlmRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/integrations/llm",
  component: function IntegrationsGate() {
    return (
      <RoleGate allow={(r) => r === "owner" || r === "admin"}>
        <IntegrationsLLMPage />
      </RoleGate>
    );
  },
});

const integrationsTwilioRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/integrations/twilio",
  component: function IntegrationsTwilioGate() {
    // Owner / Admin / Viewer / Support read; the page itself enforces
    // write-permission gating per RBAC matrix.
    return (
      <RoleGate allow={(r) => r === "owner" || r === "admin" || r === "viewer" || r === "support"}>
        <IntegrationsTwilioPage />
      </RoleGate>
    );
  },
});

const phoneNumbersRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/phone-numbers",
  component: function PhoneNumbersGate() {
    return (
      <RoleGate allow={(r) => r === "owner" || r === "admin" || r === "viewer"}>
        <PhoneNumbersPage />
      </RoleGate>
    );
  },
});

// WP-D additions ----------------------------------------------------------
const conversationsRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/conversations",
  component: ConversationsPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/notifications",
  // Owner / Admin can write; Viewer can read; Support is hidden entirely.
  component: function NotificationsGate() {
    return (
      <RoleGate allow={(r) => r !== "support"}>
        <NotificationsPage />
      </RoleGate>
    );
  },
});

const usageRoute = createRoute({
  getParentRoute: () => tenantLayoutRoute,
  path: "/usage",
  component: function UsageGate() {
    return (
      <RoleGate allow={(r) => r !== "support"}>
        <UsagePage />
      </RoleGate>
    );
  },
});

// Super routes
const superLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_super",
  component: SuperLayout,
});
const superTenantsRoute = createRoute({
  getParentRoute: () => superLayoutRoute,
  path: "/super/tenants",
  component: SuperTenantsPage,
});
const superTenantDetailRoute = createRoute({
  getParentRoute: () => superLayoutRoute,
  path: "/super/tenants/$id",
  component: SuperTenantDetailPage,
});
const superAuditRoute = createRoute({
  getParentRoute: () => superLayoutRoute,
  path: "/super/audit-logs",
  component: SuperAuditLogsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  publicLayoutRoute.addChildren([
    loginRoute,
    registerRoute,
    verifyEmailRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    acceptInvitationRoute,
  ]),
  onboardingRoute,
  tenantLayoutRoute.addChildren([
    dashboardRoute,
    membersRoute,
    profileRoute,
    tenantAuditRoute,
    sitesRoute,
    chatbotsListRoute,
    chatbotDetailRoute,
    integrationsLlmRoute,
    integrationsTwilioRoute,
    phoneNumbersRoute,
    conversationsRoute,
    notificationsRoute,
    usageRoute,
  ]),
  superLayoutRoute.addChildren([superTenantsRoute, superTenantDetailRoute, superAuditRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Helper export for layouts to render auth-aware navigation
export { Link };
